import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MessageCircle, Mail, Instagram, MapPin } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { getContent } = useSiteContent();
  const [whatsappLink, setWhatsappLink] = useState('');

  useEffect(() => {
    fetchWhatsAppLink();
  }, []);

  const fetchWhatsAppLink = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/link`);
      setWhatsappLink(response.data.whatsapp_link);
    } catch (error) {
      console.error('Error fetching WhatsApp link:', error);
    }
  };

  return (
    <div data-testid="contact-page" className="tattoo-bg-functional min-h-screen">
      <Header />

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20">
        
        {/* Hero Section */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-7xl md:text-8xl font-bold mb-8 leading-none">
              {getContent('contact_hero_title', "LET'S TALK")}
            </h1>
            <p className="font-body text-xl max-w-2xl leading-relaxed">
              {getContent('contact_hero_text', "Have questions? Ready to book? Want to discuss your tattoo idea? We're here and excited to hear from you.")}
            </p>
          </motion.div>
        </section>

        {/* Contact Methods */}
        <section className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="paper-texture p-10 tape-top-left"
              data-testid="whatsapp-contact"
            >
              <div className="mb-6">
                <MessageCircle size={48} className="mb-4" />
                <h3 className="font-heading text-3xl font-bold mb-2">WhatsApp</h3>
                <p className="font-body text-lg text-gray-600">Chat with us directly</p>
              </div>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-contact-link"
                className="inline-block bg-black text-white px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
              >
                MESSAGE US →
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="paper-texture p-10 tape-top-right"
              data-testid="email-contact"
            >
              <div className="mb-6">
                <Mail size={48} className="mb-4" />
                <h3 className="font-heading text-3xl font-bold mb-2">Email</h3>
                <p className="font-body text-lg text-gray-600">vision.tttlab@gmail.com</p>
              </div>
              <a
                href="mailto:vision.tttlab@gmail.com"
                data-testid="email-contact-link"
                className="inline-block bg-black text-white px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
              >
                SEND EMAIL →
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="paper-texture p-10 tape-scrapbook"
              data-testid="instagram-contact"
            >
              <div className="mb-6">
                <Instagram size={48} className="mb-4" />
                <h3 className="font-heading text-3xl font-bold mb-2">Instagram</h3>
                <p className="font-body text-lg text-gray-600">@vision.ttt</p>
              </div>
              <a
                href="https://www.instagram.com/vision.ttt?igsh=aXdocmlzNGtjcDEx&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-contact-link"
                className="inline-block bg-black text-white px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
              >
                FOLLOW US →
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="paper-texture p-10 tape-corner"
              data-testid="location-contact"
            >
              <div className="mb-6">
                <MapPin size={48} className="mb-4" />
                <h3 className="font-heading text-3xl font-bold mb-2">Visit</h3>
                <p className="font-body text-base text-gray-600">Vision Tattoos,<br />Fourth Floor, Satya Tower,<br />Vibhuti Khand, Gomti Nagar,<br />Lucknow-226010</p>
              </div>
              <a
                href="https://maps.google.com/?q=Vision+Tattoos+Satya+Tower+Vibhuti+Khand+Gomti+Nagar+Lucknow"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-contact-link"
                className="inline-block bg-black text-white px-8 py-3 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-all"
              >
                GET DIRECTIONS →
              </a>
            </motion.div>
          </div>
        </section>

        {/* Studio Hours */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="border-t-2 border-black pt-8"
          >
            <h2 className="font-heading text-5xl font-bold mb-8">{getContent('contact_hours_title', 'STUDIO HOURS')}</h2>
            <div className="paper-texture p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-heading text-2xl font-bold mb-4">{getContent('contact_hours_open', 'OPEN DAILY')}</h3>
                  <p className="font-body text-xl">{getContent('contact_hours_time', '12:00 PM - 7:00 PM')}</p>
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-bold mb-4">{getContent('contact_appointment_label', 'BY APPOINTMENT ONLY')}</h3>
                  <p className="font-body text-base text-gray-600">{getContent('contact_appointment_text', 'Contact us to schedule your session')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;